import { Checkbox, DateTimeInput, Field, NumericInput, RadioGroup, TextInput, TextOutput } from 'Components/Core';
import { NamePrefixModifier, withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { FieldSet, ListFieldSection } from 'Components/Layout';
import { DateTimeStyle } from 'Components/Layout/Grid/types';
import {
  WorksheetBalanceFieldSet,
  WorksheetEquipmentDropdown,
  WorksheetGridFieldSection,
  WorksheetRecordGrid,
} from 'Components/Worksheets/Core';
import { IWorksheetComponent } from 'Components/Worksheets/Core/types';
import { createDryMassEntryOptionSelector } from 'Components/Worksheets/MoistureContentWorksheet';
import { DryingEquipment } from 'Components/Worksheets/PsdSieveAnalysisWorksheet/types';
import { valueOrDefault } from 'Lib/Any';
import { pipe } from 'Lib/Hooks/Selectors';
import React from 'react';
import { useSelector } from 'react-redux';
import { getDryMassGridFieldSet } from '../gridFieldSets';

let SievingWashWashingSection: React.FC<IWorksheetComponent> = ({ createWorksheetSelector }) => {
  const dryMassEntryOption = useSelector(createDryMassEntryOptionSelector(createWorksheetSelector!));

  const showSingleReadingSection = useSelector(
    pipe(
      createWorksheetSelector!('Masses.MeasuredWashedConstantDryMass.DryingTray.DryMassReadings'),
      (r) => (r?.length ?? 0) <= 1 && dryMassEntryOption === 'Never'
    )
  );

  const dryingEquipment = useSelector(
    createWorksheetSelector!('Masses.MeasuredWashedConstantDryMass.Equipment.DryingEquipment')
  ) as DryingEquipment;

  const showTimeInOven = useSelector(
    pipe(createDryMassEntryOptionSelector(createWorksheetSelector!), (v) => v !== 'Never')
  );

  const measureWashedPortionWetMass = useSelector(
    pipe(
      createWorksheetSelector!('Masses.MeasuredWashedConstantDryMass.Preparation.MeasureWashedPortionWetMass'),
      valueOrDefault(false)
    )
  );

  return (
    <>
      <ListFieldSection>
        <Field
          input={<RadioGroup />}
          name="Preparation.IsDispersantUsed"
          options={[
            { label: 'Not used', value: false },
            { label: 'Used', value: true },
          ]}
        />
      </ListFieldSection>
      <ListFieldSection>
        <Field
          input={<WorksheetEquipmentDropdown />}
          equipmentType="sieve"
          name="Equipment.WashingSieve"
          queryParams={{ SieveSize: '75 μm' }}
        />
        <Field
          input={<WorksheetEquipmentDropdown />}
          equipmentType="sieve"
          name="Equipment.GuardSieve"
          queryParams={{ SieveSize: { value: '2 mm', operator: '>=' } }}
        />
      </ListFieldSection>

      {/* TODO: everything from this point down is identical in the hydrometer and sieving components,
      refactor to use a shared component. */}
      <NamePrefixModifier appendNamePrefix={'Masses.MeasuredWashedConstantDryMass'}>
        <ListFieldSection>
          <WorksheetBalanceFieldSet name="Equipment.Balance" />
        </ListFieldSection>
        <ListFieldSection>
          <FieldSet>
            <Field input={<TextInput />} name="DryingTray.ContainerLabel" maxLength={20} />
            <Field input={<Checkbox />} name="Preparation.MeasureWashedPortionWetMass" />
          </FieldSet>

          <Field input={<NumericInput />} name="DryingTray.ContainerMass" />
          <Field
            input={<NumericInput />}
            name="DryingTray.WetSoilAndContainerMass"
            visible={measureWashedPortionWetMass}
          />
          <Field input={<TextOutput />} name="DryingTray.WetSoilMass" visible={measureWashedPortionWetMass} />
          <Field
            input={<RadioGroup />}
            name="Equipment.DryingEquipment"
            options={[
              { label: 'Oven', value: DryingEquipment.Oven },
              { label: 'Microwave Oven', value: DryingEquipment.MicrowaveOven },
              { label: 'Infrared Light', value: DryingEquipment.InfraredLight },
              { label: 'Hotplate', value: DryingEquipment.Hotplate },
            ]}
          />
          <Field
            input={<WorksheetEquipmentDropdown />}
            name="Equipment.Oven"
            equipmentType="oven"
            visible={dryingEquipment === DryingEquipment.Oven}
          />
          <Field
            input={<WorksheetEquipmentDropdown />}
            name="Equipment.MicrowaveOven"
            equipmentType="microwave-oven"
            visible={dryingEquipment === DryingEquipment.MicrowaveOven}
          />
          <Field
            input={<WorksheetEquipmentDropdown />}
            name="Equipment.InfraredLight"
            equipmentType="infrared-light"
            visible={dryingEquipment === DryingEquipment.InfraredLight}
          />
          <Field
            input={<WorksheetEquipmentDropdown />}
            name="Equipment.Hotplate"
            equipmentType="hotplate"
            visible={dryingEquipment === DryingEquipment.Hotplate}
          />
          <Field
            input={<NumericInput />}
            name="Equipment.DryingTemperature"
            visible={dryingEquipment === DryingEquipment.Oven}
          />
          {showTimeInOven && <Field input={<DateTimeInput />} name="DryingTray.DateTimePlacedInOvenLocal" />}
          <WorksheetBalanceFieldSet name="Equipment.DryMassBalance" />
        </ListFieldSection>
        {showSingleReadingSection ? (
          <ListFieldSection>
            <Field
              input={<NumericInput />}
              name="DryingTray.DryMassReadings.0.DrySoilAndContainerMass"
              // We do not show the dry mass calculated from the Tare + Dry mass, so we need to show its messages on this field
              additionalMessagePaths={['Masses.DryingTray.0.DrySoilMass']}
            />
          </ListFieldSection>
        ) : (
          <WorksheetGridFieldSection name="DryingTray.DryMassReadings">
            <WorksheetRecordGrid
              name="DryingTray.DryMassReadings"
              orientation="vertical"
              recordProps={{
                enforceAtLeastOneRecord: true,
                preventRemove: 'all-but-last',
                preventCreate: dryMassEntryOption === 'Never',
                removeConfirm: {
                  confirm: true,
                  confirmTitle: 'Delete Dry Mass Reading',
                  confirmText: 'Are you sure you want to delete the dry mass reading? Data cannot be recovered.',
                  confirmButtonText: 'Delete',
                },
              }}
              fieldSet={getDryMassGridFieldSet(dryMassEntryOption, DateTimeStyle.Combined)}
            />
          </WorksheetGridFieldSection>
        )}
      </NamePrefixModifier>
    </>
  );
};

SievingWashWashingSection = withInjectedProps(SievingWashWashingSection);

export default SievingWashWashingSection;
export { SievingWashWashingSection as WashingSection };
